'use strict';
/* ==========================================================================
   levels/ballad.js — step "ballad" (any): THE BROADSIDE, "Write It Down" (DESIGN.md §5.4), a fife-and-drum ballad with
   karaoke, printed on a 1787 broadside. The machinery (song clock, <audio> stream, karaoke band, portrait dock,
   controls, completion) is lessons/free-speech/levels/ballad.js's (via lessons/theory/levels/song.js: the sung-word
   SFX); the stage is this lesson's (the ledger, the counter, the F / A woodtype, the print-shop props).

   ► The song: assets/music/ballad.m4a = ACE-Step take fife_b100d110_21 (100 bpm sheet, 110 s; assets/ASSETS.md).
     Timings: assets/music/ballad_words.json (tools/song_timing.py): the TRUE lyric lines of content/ballad.json (23
     lines, every one sung; the last word "Amended!" is sung but blurred, and kept) with word times from the take's
     Whisper passes, and the beat grid {bpm 99, offset = first downbeat ≈ 2.35 s}.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact. Every picture beat is keyed
   to a sung word (S.W(line, word)). Every vote, date, count and margin on the stage comes from core-local/ratify.js
   (Ratify.STATES / convention / bor / article7 / ratifiedBy order); the ledger, counter, side cards and props are the
   video's kit (window.RatifyKit, levels/video.js part 1).

   The stage (always on): parchment, bunting on the beat, and THE BROADSIDE (the broadside cut-out, left): the song's
   record, typed onto the sheet line by line as the song reaches each fact ("write it down"): the Constitution signed
   (Sep 1787), 9 of 13 must ratify, the Federalists' view, the ninth state, the Anti-Federalists' view, Massachusetts
   and New York's votes, House 17 / Senate 12 / states 10, the Bill of Rights (Dec 15, 1791); AMENDED at the end.
   Acts (the right of the stage), keyed to lyric-line indices (content/ballad.json order):
     intro    0      the title; the printing press on "press"
     verse1   1–4    1787 (woodtype) on "seventeen", the 1787 newspaper on "printed", the tally board "9 of 13" on
                     "Nine", the navy F card "FEDERALISTS: the Articles are too weak" on "Federalists", "a stronger
                     national government" on "stronger"
     chorus1  5–8    THE LEDGER: rows fill in ratification order, the counter "RATIFIED: n of 9 needed" ticks one row at a
                     time and reaches NINE on the sung "Nine" (New Hampshire; Ratify.ninth()); the bell rings; the
                     brick A "rights in writing!" on "no"; the quill takes the bell's place on "Write it down and we'll sign"
     verse2   9–12   the brick A card "ANTI-FEDERALISTS: loved the country too"; "feared a strong national government";
                     the ballot box + Massachusetts 187–168 on "voted", "with recommended amendments" on "list"; the
                     New York card 30–27 (the tally bar) on "New York", "a margin of 3" on "close"
     bridge   13–16  House 17 → Senate 12 → states 10 (Ratify.bor) on their numbers; 1791 + the rights scroll on "Bill";
                     "lost the vote, won the Bill of Rights" on "won"
     chorus2  17–20  the ledger again: nine on "Nine", then the last four (NY… RI) on "Write it down … tonight":
                     RATIFIED 13 of 13
     outro    21–22  the quill writes; AMENDED (stamp + wax seal) on the sung "Amended!"
   Karaoke (bottom band, a printed strip): the current line in slab type, the sung word on a mustard scrap,
   Federalist(s) / yes in navy, Anti-Federalist(s) / no in brick, "Write it down" in ransom letters, the next line faint.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom;
   "Finish" once ≥ 90 % has been heard. The song's end (≥ 90 % heard) → Game.complete('ballad').
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared <audio>
   element (Audio.media), never decoded into memory.
   SFX (tier ballad): the bell (bell_ring) on each chorus's "Nine", the quill (shared scribble) on "sign" and the outro's
   "Write", the stamp on "Amended!"; none while the clock runs fast (tests) or jumps (a seek).

   QA hooks: sc.song (S(i), E(i), W(i, word), beats), sc.plan.acts, sc.plan.sfx, sc.debugAt(t) (freeze at song time t),
   sc.now(), sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done() / sc.finishStub() (stamp now),
   sc.lyricAt(t), sc.activeWord(t), sc.actAt(t), sc.counterAt(t) (the ledger counter: rows ratified), sc.rowsAt(t)
   (the codes filled), sc.recordAt(t) (the broadside lines printed so far), sc.heard, sc.ended, sc.audioReady;
   Levels.ballad.record() (every broadside line + the numbers it shows, from Ratify).
   ========================================================================== */
(function () {
  const STEP = 'ballad';
  const TAKE_NAME = 'fife_b100d110_21';
  const AUDIO = 'music/ballad.m4a', WORDS = 'music/ballad_words.json';
  const KEY = 'song:ballad';
  const WOOD = "'Alfa Slab One'", TYPE = "'Special Elite'";
  const CC = () => Civic.C;
  const RK = () => window.RatifyKit;
  const R = () => window.Ratify;
  const INK = '#2b2420', CHALK = 'rgba(244,240,226,0.95)';
  const BAND_Y = 832, CUR_Y = 912, NEXT_Y = 1012, CUR_SIZE = 56, NEXT_SIZE = 36;
  const FADE = 0.35;                                  // act cross-fade (s)
  const THRESH = 0.9;                                 // share of the song heard for the stamp / "Finish"
  const CREDIT = 'AI-generated song · the history is real';
  const END_NOTE = 'Written down, in black and white.';
  const DASH = '–';

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 110;
      const b = d.beats || {};
      this.bpm = b.bpm || 100; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off - 0.05 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
  }

  /* ---------------- the broadside's record: every line from the model (Ratify) ---------------- */
  function record() {
    const Rt = R(), cv = Rt.convention, b = Rt.bor, ma = Rt.st('MA'), ny = Rt.st('NY'), nine = Rt.st(Rt.ninth());
    const v = s => `${s.yes}${DASH}${s.no}`;
    return [
      { id: 'plan', side: null, text: `${cv.shown}: the Constitution signed`, nums: [cv.year] },
      { id: 'nine', side: null, text: `${Rt.article7} of ${Rt.count()} states must ratify`, nums: [Rt.article7, Rt.count()] },
      { id: 'fed', side: 'federalist', text: 'Federalists: a stronger national government', nums: [] },
      { id: 'ninth', side: null, text: `${nine.name}, ${nine.shown}: number ${Rt.article7}`, nums: [nine.order] },
      { id: 'anti', side: 'anti', text: 'Anti-Federalists: rights in writing', nums: [] },
      { id: 'ma', side: null, text: `${ma.name} ${v(ma)}, with amendments`, nums: [ma.yes, ma.no] },
      { id: 'ny', side: null, text: `${ny.name} ${v(ny)}`, nums: [ny.yes, ny.no] },
      { id: 'bor', side: null, text: `House ${b.house}, Senate ${b.senate}, states ${b.ratified}`, nums: [b.house, b.senate, b.ratified] },
      { id: 'date', side: null, text: `${b.date}: the Bill of Rights`, nums: [b.year] },
    ];
  }

  /* ---------------- the plan: acts, cue times, the ledger rows, the SFX ---------------- */
  function plan(S) {
    const b = i => S.S(i) - 0.3;
    const acts = [
      { id: 'intro', at: 0, draw: actIntro },
      { id: 'verse1', at: b(1), draw: actVerse1 },
      { id: 'chorus1', at: b(5), draw: actChorus, c0: 5 },
      { id: 'verse2', at: b(9), draw: actVerse2 },
      { id: 'bridge', at: b(13), draw: actBridge },
      { id: 'chorus2', at: b(17), draw: actChorus, c0: 17 },
      { id: 'outro', at: b(21), draw: actOutro },
    ];
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur + 1; });
    // the ledger rows of each chorus: rows 1..9 from the chorus's first word to the sung "Nine" (one per step, the
    // ninth exactly on "Nine"); chorus 2 fills the last four on "Write it down … tonight" (every one of the 13)
    const Rt = R(), need = Rt.article7;
    acts.filter(a => a.c0 != null).forEach(a => {
      const c0 = a.c0, t0 = S.S(c0) + 0.2, t9 = S.W(c0 + 2, 'Nine'), rows = {};
      Rt.STATES.forEach((st, k) => {
        if (k < need) rows[st.code] = t0 + k * (t9 - t0) / (need - 1);
        else if (c0 === 17) rows[st.code] = S.W(c0 + 3, ['Write', 'down', 'sign', 'tonight'][Math.min(3, k - need)]) + (k - need > 3 ? 0.3 * (k - need - 3) : 0);
      });
      a.rows = rows; a.nine = t9;
    });
    // the broadside's record: each line typed on its sung word
    const rec = record(), when = {
      plan: S.W(1, 'plan'), nine: S.W(2, 'states'), fed: S.W(4, 'government'), ninth: S.W(7, 'Nine') + 0.2, anti: S.W(7, 'no'),
      ma: S.W(11, 'list'), ny: S.W(12, 'twenty'), bor: S.W(14, 'ten'), date: S.W(15, 'true'),
    };
    rec.forEach(r => { r.t = when[r.id]; });
    const sfx = [
      { name: ['bell_ring', 'ding'], t: S.W(7, 'Nine'), gain: 0.5 },
      { name: ['quill_scratch', 'scribble'], t: S.W(8, 'sign'), gain: 0.3 },
      { name: ['bell_ring', 'ding'], t: S.W(19, 'Nine'), gain: 0.5 },
      { name: ['quill_scratch', 'scribble'], t: S.W(20, 'sign'), gain: 0.3 },
      { name: ['quill_scratch', 'scribble'], t: S.W(21, 'Write'), gain: 0.3 },
    ];
    const tStamp = S.W(22, 'Amended');
    return { acts, ticks: acts.slice(1).map(a => a.at), rec, sfx, tStamp };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  /* the chorus act at t (null outside the choruses) */
  function chorusAt(p, t) { const A = p.acts[actAt(p, t)]; return A.c0 != null ? A : null; }
  const rowsFilled = (A, t) => (A ? R().STATES.filter(st => A.rows[st.code] != null && t >= A.rows[st.code]).map(st => st.code) : []);

  /* ---------------- drawing helpers ---------------- */
  const layoutCache = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function measure(str, font) {
    const k = font + '|' + str;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function ransomW(str, size, seed) {
    const k = 'R|' + str + '|' + size + '|' + seed;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); w = ransom(str, -9999, -9999, size, 1e9, { seed }); g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function withAlpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  const txt = (str, x, y, size, o = {}) => RK().txt(str, x, y, size, o);
  const tag = (str, x, y, size, s, o) => RK().tag(str, x, y, size, s, o);
  const fit = (str, maxW, size, font) => RK().fitSize(str, maxW, size, font);
  /* a prop: the engraving cut-out (IMG[kind]) when loaded, else the video kit's stand-in */
  const prop = (kind, x, y, h, o) => RK().prop(kind, x, y, h, o);

  /* ---------------- the stage: background, bunting, the broadside ---------------- */
  const BS = { x: 318, y: 478, h: 690, sx: 1.45 };     // the broadside cut-out (358×640): centre, drawn height, widened ×1.45
  function backdrop(S, t, docked) {
    const C = CC();
    g.fillStyle = paperTile(C.parchment, 6); g.fillRect(0, 0, 1920, 1080);
    const pu = S.pulse(t, 6), x1 = docked ? 1890 : Math.min(1580, UI.hudLeft(3) - 30);
    g.save(); g.translate(0, 5 * pu);
    Civic.bunting(docked ? 30 : 560, x1, 4, { sag: docked ? 12 : 20, n: docked ? 17 : 10, seed: 5, sway: 1 + pu });
    g.restore();
  }
  /* the broadside's text box: inside the printed border (~0.13–0.87 of the width), between the woodcut ornaments
     (~0.125–0.77 of the height, measured on the cut-out) */
  function sheetBox() {
    const w = (IMG.broadside ? BS.h * IMG.broadside.width / IMG.broadside.height : BS.h * 0.56) * BS.sx;
    return { x0: BS.x - w * 0.37, x1: BS.x + w * 0.37, y0: BS.y - BS.h * 0.375, y1: BS.y + BS.h * 0.27, w };
  }
  /* the record's layout: the lines wrapped to the sheet, the font shrunk until they all fit (cached once the fonts are in) */
  let recLay = null;
  function recordLayout(rec) {
    if (recLay && recLay.n === rec.length) return recLay;
    const B = sheetBox(), maxW = B.x1 - B.x0, top = B.y0 + 92, room = B.y1 - top;
    let size = 26, out;
    for (; size >= 16; size--) {
      const f = `${size}px ${TYPE}`, lh = Math.round(size * 1.18), gap = Math.round(size * 0.42);
      let y = 0; out = [];
      for (const r of rec) { const ls = UI._wrap(r.text, maxW - 16, f); out.push({ r, ls, y, lh }); y += ls.length * lh + gap; }
      if (y - gap <= room) break;
    }
    const L = { n: rec.length, size, top, rows: out, B };
    if (fontOk()) recLay = L;
    return L;
  }
  function broadside(S, p, t) {
    const C = CC(), B = sheetBox(), s = pop(T, 0.15, 0.4, 1.8);
    if (s <= 0.001) return;
    g.save(); g.translate(BS.x, BS.y); g.scale(s, s); g.translate(-BS.x, -BS.y);
    if (IMG.broadside) cut('broadside', BS.x, BS.y, BS.h, { sx: BS.sx, rot: -0.008, seed: 3101, depth: 1.3 });
    else { paperRect(BS.x, BS.y, Math.round(B.w), BS.h, '#f6eedb', { seed: 3101, depth: 1.3, tex: 'tex_news' }); }
    tape(BS.x, BS.y - BS.h / 2 + 6, 120, 0.05, undefined, 3102);
    // the masthead: the song's title in woodtype, a double rule
    const title = ((Game.content.song && Game.content.song.title) || 'Write It Down').toUpperCase();
    const tsz = Math.min(30, Math.floor(30 * (B.x1 - B.x0 - 10) / Math.max(1, measure(title, `30px ${WOOD}`) * 1.12)));
    Civic.woodtype(title, BS.x, B.y0 + 20, tsz, { color: C.navy, ink: C.cream, seed: 3103, stagger: 0 });
    txt('A NEW BALLAD', BS.x, B.y0 + 60, 19, { font: TYPE, color: 'rgba(43,36,32,0.75)' });
    ink([[B.x0, B.y0 + 76], [B.x1, B.y0 + 76]], { w: 2.5, color: INK, seed: 3104, passes: 1, step: 60 });
    ink([[B.x0, B.y0 + 82], [B.x1, B.y0 + 82]], { w: 1.2, color: INK, seed: 3105, passes: 1, step: 60 });
    // the record, typed line by line
    const Lo = recordLayout(p.rec), f = `${Lo.size}px ${TYPE}`;
    Lo.rows.forEach(({ r, ls, y, lh }, k) => {
      if (t < r.t) return;
      const col = r.side ? Civic.SIDE[r.side].color : INK;
      ls.forEach((l, j) => {
        const t0 = r.t + j * 0.25, u = clamp((t - t0) / Math.max(0.2, l.length * 0.025));
        if (u <= 0) return;
        const yy = Lo.top + y + (j + 0.5) * lh;
        g.save(); g.font = f; g.fillStyle = col; g.textAlign = 'left'; g.textBaseline = 'middle';
        if (u < 1) { g.beginPath(); g.rect(B.x0 + 4, yy - lh, (B.x1 - B.x0) * u + 4, lh * 2); g.clip(); }
        g.fillText(l, B.x0 + 8, yy);
        g.restore();
      });
      // a fresh line gets a brief mustard underline
      const fresh = 1 - P(t, r.t + 0.8, r.t + 2.2);
      if (fresh > 0) withAlpha(fresh * 0.8, () => ink([[B.x0 + 6, Lo.top + y + ls.length * lh], [B.x0 + 6 + Math.min(B.x1 - B.x0 - 12, measure(ls[ls.length - 1], f) + 4), Lo.top + y + ls.length * lh]], { w: 5, color: PAL.mustard, seed: 3120 + k, prog: E.outCubic(P(t, r.t, r.t + 0.5)), passes: 1 }));
    });
    // AMENDED on the sheet, and a wax seal, on the sung "Amended!"
    if (t >= p.tStamp) {
      UI.stamp('AMENDED', BS.x, B.y1 + 64, { rot: -0.08, s: pop(T, p.tStamp, 0.3, 2.2) * 0.62, color: C.brick });
      Civic.seal(B.x1 + 16, B.y1 + 96, 40, { s: pop(T, p.tStamp + 0.3, 0.35, 2.4), rot: 0.2, seed: 3130 });
    }
    g.restore();
  }

  /* ======================================================================
     the acts (the right of the stage)
     ====================================================================== */
  const RX = 1230;                                     // the centre of the right-hand stage
  function actIntro(S, t) {
    const C = CC(), tP = S.W(0, 'press');
    ransom('WRITE IT DOWN', RX, 190, 92, 0.1, { seed: 3201 });
    hand('a fife-and-drum ballad', RX, 290, 50, 0.5, { color: C.navy, seed: 3202, dur: 0.5 });
    prop('printing_press', RX, 560, 380, { s: pop(T, Math.min(tP, 1.2), 0.4, 1.8), seed: 3203, rot: 0.01 });
  }
  function actVerse1(S, t) {
    const C = CC(), Rt = R();
    const t17 = S.W(1, 'seventeen'), tPr = S.W(1, 'printed'), tNi = S.W(2, 'Nine'), tSt = S.W(2, 'stand');
    const tFe = S.W(3, 'Federalists'), tAr = S.W(3, 'Articles'), tWe = S.W(3, 'weak'), tSg = S.W(4, 'stronger');
    if (t >= t17) Civic.woodtype(String(Rt.convention.year), 880, 170, 80, { t0: t17, color: C.cream, ink: C.navy, seed: 3301, stagger: 0.05 });
    if (t >= tPr) prop('newspaper_1787', 880, 420, 300, { s: pop(T, tPr, 0.35, 2.0), rot: -0.05, seed: 3302 });
    if (t >= tNi) {
      const s = pop(T, tNi, 0.35, 2.0);
      prop('tally_board', 1560, 250, 280, { s, rot: 0.03, seed: 3303 });
      if (s > 0.9) {
        g.save(); g.translate(1560, 250); g.rotate(0.03);
        hand(`${Rt.article7} of ${Rt.count()}`, 100, -18, 58, tNi + 0.1, { color: CHALK, seed: 3304, dur: 0.4 });
        hand('states', 100, 42, 38, tSt, { color: CHALK, seed: 3305, dur: 0.3 });
        g.restore();
      }
    }
    if (t >= tFe - 0.1) {
      RK().sideCard('federalist', 1330, 560, pop(T, tFe - 0.1, 0.35, 1.8), { w: 700, h: 200, seed: 3310, rot: -0.015, line: t >= tAr ? 'the Articles are too weak' : '', t0: tAr });
    }
    if (t >= tSg) tag('a stronger national government', 1330, 732, 42, pop(T, tSg, 0.35, 2.0), { color: '#fffdf7', ink: C.navy, seed: 3312, rot: 0.015 });
  }
  const LEDGER = { x: 1116, y: 446, w: 920, h: 740 };
  function actChorus(S, t, A) {
    const C = CC(), Rt = R(), K = RK(), c0 = A.c0, rows = A.rows;
    K.ledger(LEDGER.x, LEDGER.y, LEDGER.w, LEDGER.h, st => {
      const r = rows[st.code];
      return r == null ? {} : { name: r - 0.15, month: r - 0.1, bar: r, hi: st.code === Rt.ninth() && t >= A.nine ? 1 : 0 };
    }, { seed: 3401 });
    const n = rowsFilled(A, t).length, pu = S.pulse(t, 7);
    g.save(); g.translate(1742, 268); g.scale(1 + 0.03 * pu, 1 + 0.03 * pu);
    K.counter(0, 0, n, 0.78 * pop(T, A.at + 0.1, 0.35, 1.6), { pulse: t > A.nine ? P(t, A.nine, A.nine + 0.4) : 0 });
    g.restore();
    // the bell rings at nine; on "Write it down and we'll sign" the quill takes its place (writing)
    const tW = S.W(c0 + 3, 'Write'), tE = S.E(c0 + 3);
    const ring = t > A.nine ? Math.sin((t - A.nine) * 11) * 0.35 * Math.exp(-(t - A.nine) * 1.2) : 0;
    withAlpha(1 - P(t, tW - 0.35, tW), () => prop('bell', 1742, 530, 190, { s: pop(T, A.at + 0.3, 0.4), seed: 3402, swing: ring }));
    if (t >= tW - 0.1) prop('quill_pen', 1742, 520, 200, { s: pop(T, tW - 0.1, 0.35, 2.0), rot: t < tE ? 0.06 * Math.sin((t - tW) * 16) : 0, seed: 3406 });
    if (t >= A.nine) tag(`${Rt.name(Rt.ninth())} makes ${K.numWord(Rt.article7)}`, 1742, 660, 30, pop(T, A.nine + 0.2, 0.35), { color: '#f2d479', ink: C.navy, seed: 3403, rot: -0.03 });
    // "but the no side said": the brick A, rights in writing
    const tNo = S.W(c0 + 2, 'no');
    if (t >= tNo) {
      K.sideBadge('anti', 1628, 758, 66, pop(T, tNo, 0.3, 2.0), -0.08, 3404);
      hand('rights in writing!', 1782, 758, 36, tNo + 0.15, { color: C.brick, seed: 3405, dur: 0.4 });
    }
  }
  function actVerse2(S, t) {
    const C = CC(), Rt = R(), K = RK(), ma = Rt.st('MA'), ny = Rt.st('NY');
    const tAn = S.W(9, 'Anti'), tLo = S.W(9, 'loved'), tFe = S.W(10, 'feared'), tVo = S.W(11, 'voted'), tLi = S.W(11, 'list');
    const tNY = S.W(12, 'New'), tCl = S.W(12, 'close');
    if (t >= tAn - 0.1) K.sideCard('anti', 1020, 176, pop(T, tAn - 0.1, 0.35, 1.8), { w: 760, h: 200, seed: 3501, rot: 0.012, line: t >= tLo ? 'loved the country too' : '', t0: tLo });
    if (t >= tFe) tag('feared a strong national government', 1020, 330, 40, pop(T, tFe, 0.35, 2.0), { color: '#fffdf7', ink: C.brick, seed: 3502, rot: -0.012 });
    // Massachusetts: the ballot box, the vote, the list of amendments
    if (t >= tVo) {
      prop('ballot_box', 850, 560, 230, { s: pop(T, tVo, 0.35, 2.0), rot: -0.03, seed: 3503 });
      tag(`${ma.name} ${K.vote(ma.yes, ma.no)}`, 850, 712, 40, pop(T, tVo + 0.15, 0.35, 2.0), { font: WOOD, color: C.cream, ink: INK, seed: 3504, rot: 0.02 });
    }
    if (t >= tLi) {
      const s = pop(T, tLi, 0.35, 2.0);
      g.save(); g.translate(1120, 560); g.rotate(0.05); g.scale(s, s);
      paperRect(0, 0, 230, 250, '#fffdf7', { seed: 3505, depth: 1.1 });
      tape(0, -122, 90, -0.05, undefined, 3506);
      txt('recommended', 0, -84, 26, { font: TYPE, color: C.brick });
      txt('amendments', 0, -54, 26, { font: TYPE, color: C.brick });
      for (let k = 0; k < 5; k++) ink([[-86, -16 + k * 30], [80 - (k % 2) * 30, -16 + k * 30]], { w: 3, color: 'rgba(43,36,32,0.55)', seed: 3507 + k, passes: 1, prog: P(t, tLi + 0.2 + k * 0.1, tLi + 0.4 + k * 0.1) });
      g.restore();
    }
    // New York: 30–27, the tally bar, the margin
    if (t >= tNY - 0.15) {
      const s = pop(T, tNY - 0.15, 0.35, 1.8), bw = 440, share = ny.yes / (ny.yes + ny.no);
      g.save(); g.translate(1580, 580); g.rotate(-0.02); g.scale(s, s);
      paperRect(0, 0, 560, 300, C.cream, { seed: 3510, depth: 1.3 });
      paperRect(0, -128, 552, 30, C.navy, { seed: 3511, depth: 0 });
      Civic.woodtype(ny.name.toUpperCase(), 0, -64, 40, { color: C.navy, ink: C.cream, seed: 3512, stagger: 0 });
      txt(K.vote(ny.yes, ny.no), 0, 12, 76, { font: WOOD, color: INK });
      const grow = E.outCubic(P(t, tNY + 0.6, tNY + 1.3));
      g.fillStyle = C.navy; g.fillRect(-bw / 2, 76, bw * share * grow, 34);
      g.fillStyle = C.brick; g.fillRect(-bw / 2 + bw * share, 76, bw * (1 - share) * grow, 34);
      g.strokeStyle = 'rgba(43,36,32,0.45)'; g.lineWidth = 2; g.strokeRect(-bw / 2, 76, bw, 34);
      ink([[0, 68], [0, 118]], { w: 3, color: INK, seed: 3513, passes: 1 });
      g.restore();
      if (t >= tCl) tag(`a margin of ${Rt.margin('NY')}`, 1660, 770, 38, pop(T, tCl, 0.35, 2.0), { color: '#f2d479', ink: INK, seed: 3514, rot: 0.03 });
    }
  }
  function actBridge(S, t) {
    const C = CC(), Rt = R(), K = RK(), b = Rt.bor;
    const steps = [
      { label: 'HOUSE', n: b.house, t0: S.W(13, 'seventeen'), w: 640, color: C.navy },
      { label: 'SENATE', n: b.senate, t0: S.W(13, 'twelve'), w: 520, color: C.navy },
      { label: 'STATES', n: b.ratified, t0: S.W(14, 'ten'), w: 400, color: C.brick },
    ];
    steps.forEach((st, k) => {
      if (t < st.t0 - 0.1) return;
      const y = 190 + k * 170, s = pop(T, st.t0 - 0.1, 0.35, 2.0);
      g.save(); g.translate(960, y); g.rotate([-0.01, 0.012, -0.008][k]); g.scale(s, s);
      paperRect(0, 0, st.w, 130, C.cream, { seed: 3601 + k * 3, depth: 1.2 });
      paperRect(-st.w / 2 + 70, 0, 118, 110, st.color, { seed: 3602 + k * 3, depth: 0.8 });
      txt(String(st.n), -st.w / 2 + 70, 4, 72, { font: WOOD, color: C.cream });
      txt(st.label, 60, 4, 48, { font: WOOD, color: st.color });
      g.restore();
      if (k < 2 && t >= steps[k + 1].t0 - 0.1) ink([[960, y + 66], [960, y + 104]], { w: 6, color: INK, seed: 3610 + k, prog: E.outCubic(P(t, steps[k + 1].t0 - 0.1, steps[k + 1].t0 + 0.2)), passes: 1 });
    });
    const tY = S.W(15, 'Seventeen'), tBi = S.W(15, 'Bill'), tWo = S.W(16, 'won');
    if (t >= tY) Civic.woodtype(String(b.year), 1560, 160, 78, { t0: tY, color: C.cream, ink: C.brick, seed: 3620, stagger: 0.05 });
    if (t >= tBi) {
      prop('scroll_rights', 1560, 420, 300, { s: pop(T, tBi, 0.35, 2.0), rot: -0.04, seed: 3621 });
      tag('BILL OF RIGHTS', 1560, 600, 36, pop(T, tBi + 0.2, 0.35, 2.0), { font: WOOD, color: C.brick, ink: C.cream, seed: 3622, rot: 0.02 });
    }
    if (t >= tWo) {
      K.sideBadge('anti', 790, 720, 80, pop(T, tWo, 0.3, 2.0), -0.08, 3623);
      tag('lost the vote, won the Bill of Rights', 1260, 722, 40, pop(T, tWo + 0.15, 0.35, 2.0), { color: '#fffdf7', ink: C.brick, seed: 3624, rot: -0.012 });
    }
  }
  function actOutro(S, t, A) {
    const C = CC(), tW = S.W(21, 'Write'), tD = S.E(21);
    // the quill writes "Write it down!" in ink
    prop('quill_pen', RX + 240 + 60 * E.inOutSine(P(t, tW, tD)), 380 - 20 * Math.sin(P(t, tW, tD) * TAU * 2), 260, { s: pop(T, tW - 0.3, 0.35), rot: 0.35, seed: 3701 });
    if (t >= tW) hand('Write it down!', RX - 120, 420, 84, tW, { color: C.navy, seed: 3702, dur: Math.max(0.4, tD - tW) });
    if (t >= A.tStamp) UI.stamp('AMENDED', RX, 640, { rot: -0.1, s: pop(T, A.tStamp, 0.3, 2.2) * 1.3, color: C.brick });
  }

  function drawStage(S, p, t, docked) {
    backdrop(S, t, docked);
    const k = actAt(p, t), A = p.acts[k];
    const run = (i, a) => withAlpha(a, () => { g.save(); p.acts[i].draw(S, t, { ...p.acts[i], tStamp: p.tStamp, p }); g.restore(); });
    if (k > 0 && t < A.at + FADE) run(k - 1, 1 - P(t, A.at, A.at + FADE));
    run(k, k === 0 ? 1 : P(t, A.at, A.at + FADE * 0.6));
    broadside(S, p, t);
  }

  /* ---------------- karaoke ---------------- */
  const HOOK = ['write', 'it', 'down'];
  const WORD_COL = () => { const C = CC(); return { federalists: C.navy, federalist: C.navy, yes: C.navy, antifederalists: C.brick, antifederalist: C.brick, no: C.brick }; };
  const wordColor = w => WORD_COL()[normW(w)] || null;
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    for (let k = 0; k + HOOK.length <= ws.length; k++) if (HOOK.every((w, j) => normW(ws[k + j].w) === w)) HOOK.forEach((_, j) => out.add(k + j));
    return out;
  }
  function tokens(item) {
    const ws = item.words, hk = hookIdx(item), toks = [];
    for (let k = 0; k < ws.length; k++) {
      if (hk.has(k) && !hk.has(k - 1)) {
        const run = ws.slice(k, k + HOOK.length), last = run[run.length - 1];
        const trail = (last.w.match(/[,.!?]+$/) || [''])[0];
        toks.push({ text: run.map(w => w.w.replace(/[,.!?]+/g, '')).join(' ').toUpperCase(), s: run[0].s, e: last.e, ransom: true, trail });
        k += HOOK.length - 1; continue;
      }
      toks.push({ text: ws[k].w, s: ws[k].s, e: ws[k].e, col: wordColor(ws[k].w) });
    }
    return toks;
  }
  const lays = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (lays.has(key)) return lays.get(key);
    const toks = tokens(item), f = `${size}px ${WOOD}`, space = measure(' ', f) + size * 0.08, rs = size * 0.62;
    let x = 0;
    toks.forEach((tk, j) => {
      tk.seed = 400 + item.i * 13 + j * 5;
      if (tk.ransom) {
        tk.w = ransomW(tk.text, rs, tk.seed); tk.tw = tk.trail ? measure(tk.trail, f) : 0;
        tk.x = x + size * 0.1; x += tk.w + tk.tw + size * 0.2 + space;
      } else { tk.w = measure(tk.text, f); tk.x = x; x += tk.w + space; }
    });
    const out = { toks, width: x - space, size, rs };
    if (fontOk()) lays.set(key, out);
    return out;
  }
  const tokEnd = (toks, j) => { const tk = toks[j], nx = toks[j + 1]; return Math.min(Math.max(tk.e, tk.s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1540) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, C = CC();
    const appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25)), yy = y + (1 - a) * 36;
    const f = `${size}px ${WOOD}`;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < tokEnd(Lo.toks, j);
      if (tk.ransom) {
        if (!sung) withAlpha(0.3 * a, () => { g.font = `${Math.round(size * 0.8)}px ${TYPE}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(tk.text, x + tk.w / 2, yy); });
        else {
          const beat = 1 + 0.06 * Math.exp(-(t - tk.s) * 6);
          g.save(); g.translate(x + tk.w / 2, yy); g.scale(beat, beat);
          ransom(tk.text, 0, 0, Lo.rs, tk.s, { seed: tk.seed, stagger: clamp((tk.e - tk.s) / Math.max(1, tk.text.length), 0.02, 0.06) });
          g.restore();
        }
        if (tk.trail && sung) { g.save(); g.font = f; g.fillStyle = C.ink; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(tk.trail, x + tk.w + 6, yy); g.restore(); }
        return;
      }
      if (active) paperRect(x + tk.w / 2, yy + size * 0.04, tk.w + 24, size * 1.08, PAL.mustard, { seed: tk.seed, depth: 0.5, boil: false, s: 1 + 0.06 * Math.exp(-(t - tk.s) * 10) });
      g.save(); g.font = f; g.textAlign = 'left'; g.textBaseline = 'middle';
      if (!sung) { g.globalAlpha *= 0.3 * a; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); }
      else {
        const p = E.outQuad(P(t, tk.s, tk.s + clamp(tk.e - tk.s, 0.08, 0.3)));
        if (p < 1) { g.save(); g.globalAlpha *= 0.3; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); g.restore(); g.beginPath(); g.rect(x - 4, yy - size, (tk.w + 8) * p, size * 2); g.clip(); }
        g.fillStyle = active ? C.ink : tk.col || C.ink; g.fillText(tk.text, x, yy);
      }
      g.restore();
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) { const nx = ws[j + 1], end = Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); if (t >= ws[j].s - 0.02 && t < end) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e }; }
    return null;
  }
  /* the band: a printed strip (cream, a double rule under a torn top edge) */
  function band(song, t) {
    const C = CC();
    paperRect(960, BAND_Y + 140, 2040, 290, C.cream, { seed: 77, torn: true, depth: 1.8, boil: false, tex: 'tex_news' });
    ink([[40, BAND_Y + 14], [1880, BAND_Y + 14]], { w: 3, color: 'rgba(43,36,32,0.7)', seed: 3801, passes: 1, step: 90, amp: 0.3 });
    ink([[40, BAND_Y + 21], [1880, BAND_Y + 21]], { w: 1.2, color: 'rgba(43,36,32,0.7)', seed: 3802, passes: 1, step: 90, amp: 0.3 });
    tape(110, BAND_Y + 6, 150, -0.08, undefined, 71); tape(1810, BAND_Y + 6, 150, 0.08, undefined, 72);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) { g.save(); g.font = `28px ${TYPE}`; g.fillStyle = 'rgba(31,47,85,0.75)'; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(sec.toLowerCase(), 60, BAND_Y + 44); g.restore(); }
    if (cur) drawCurrent(cur, t, CUR_Y);
    const nt = (txt_, col, a) => { let size = NEXT_SIZE; const f = s => `${s}px ${TYPE}`; const w = measure(txt_, f(size)); if (w > 1700) size = Math.floor(size * 1700 / w);
      g.save(); g.globalAlpha *= a; g.font = f(size); g.fillStyle = col; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt_, 960, NEXT_Y); g.restore(); };
    if (next) nt(next.text, C.navy, 0.65);
    else if (song.items.length && t > song.items[song.items.length - 1].e) nt(END_NOTE, C.navy, 0.8);
    if (!cur && !next && t > 1) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      g.save(); g.font = `72px 'Segoe UI Symbol', 'DejaVu Sans', sans-serif`; g.fillStyle = C.navy; g.textAlign = 'center'; g.textBaseline = 'middle';
      ['♪', '♫', '♪'].forEach((nn, i) => g.fillText(nn, 860 + i * 100, CUR_Y - 24 * pu * (i === b ? 1 : 0.15)));
      g.restore();
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Ballad extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0; this.lastT = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try { record(); if (!window.RatifyKit) throw new Error('no RatifyKit'); } catch (e) { this.err = 'ratify model: ' + e.message; console.warn('[ballad]', this.err); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[ballad] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.lastT = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.lastT = this.pos; this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; this.lastT = t; }
    /* stamp the step now (the end of a ≥ 90 % listen, the "Finish" button, QA) */
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    finishStub() { this.done(); }
    finish() { this.done(); }
    canFinish() { return !!this.song && !this.completed && this.heard >= THRESH * this.song.dur; }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }
    rowsAt(t) { return this.plan ? rowsFilled(chorusAt(this.plan, t), t) : []; }
    counterAt(t) { return this.rowsAt(t).length; }
    recordAt(t) { return this.plan ? this.plan.rec.filter(r => t >= r.t).map(r => r.text) : []; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      const now = this.now(), fast = window.__timeScale > 1;
      // the stage's sound effects, each on its sung word (skipped when the clock jumps: a seek or a fast test run)
      if (now > this.lastT && now - this.lastT < 0.5 && !fast) for (const c of this.plan.sfx) if (c.t > this.lastT && c.t <= now) { const nm = c.name.find(x => Audio.has(x)); if (nm) Audio.play(nm, { gain: c.gain }); }
      if (this.lastT < this.plan.tStamp && now >= this.plan.tStamp && !fast) Audio.play('stamp', { gain: 0.6 });
      this.lastT = now;
      if (now >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= THRESH * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.finishRect && Math.abs(x - this.finishRect[0]) < this.finishRect[2] / 2 && Math.abs(y - this.finishRect[1]) < this.finishRect[3] / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null; this.lastT = this.pos;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.song && Game.content.song.title) || 'Write It Down';
      if (!this.song || this.err) {
        g.fillStyle = paperTile(CC().parchment, 6); g.fillRect(0, 0, 1920, 1080);
        ransom('THE BROADSIDE', 960, 400, 110, this.enteredAt + 0.2, { seed: 42 });
        const msg = this.err ? 'the song is missing (' + this.err + ')' : 'tuning the fife…';
        hand(msg, 960, 580, 54, this.enteredAt + 0.4, { color: CC().navy, seed: 44 });
        if (docked) { Dock.head(title, ''); Dock.text('bl-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawStage(this.song, this.plan, t, docked);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? END_NOTE : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the song…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('bl-end', [{ label: '↺ Play it again', color: PAL.teal, onClick: () => this.restart() },
          { label: 'Back to the map', color: CC().navy, onClick: () => Game.replace(new Levels.map()) }]);
      } else if (this.canFinish() && Game.current() === this) {
        Dock.buttons('bl-finish', [{ label: 'Finish', color: CC().brick, onClick: () => this.done() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'song position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150, 1080);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the song from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos; this.lastT = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused ▶: a dim wash and a mustard paper disc */
    pausedMark(y, r, h) {
      g.save(); g.fillStyle = 'rgba(27,21,18,0.28)'; g.fillRect(0, 0, 1920, h); g.restore();
      paperCircle(960, y, r, PAL.mustard, { seed: 63, depth: 1.6 });
      g.save(); g.fillStyle = PAL.ink; g.beginPath(); g.moveTo(960 - r * 0.32, y - r * 0.55); g.lineTo(960 + r * 0.55, y); g.lineTo(960 - r * 0.32, y + r * 0.55); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song, C = CC();
      ransom('THE BROADSIDE', 40, 56, 36, t0 + 0.1, { align: 'left', seed: 61 });
      hand('♪ ' + CREDIT, 44, 108, 26, t0 + 0.4, { align: 'left', color: C.navy, seed: 62, dur: 0.4 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('bl-map', '⌂', m.x, m.y, fx)) { Game.replace(new Levels.map()); return; }
      if (S) {
        if (UI.iconButton('bl-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('bl-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, color: PAL.mustard })) this.toggle();
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('bl-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(43,36,32,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.brick; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.navy; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, C.navy, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100, BAND_Y);
        hand(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, 48, -1e6, { dur: 0.01, color: PAL.cream, outline: 'rgba(31,47,85,0.92)', seed: 64 });
      }
      if (!this.startedOnce && !this.frozen) hand('loading the song…', 960, 590, 48, this.enteredAt + 0.3, { color: C.navy, seed: 65 });
      this.finishRect = null;
      if (this.ended && Game.current() === this) {
        if (UI.button('bl-again', '↺ Play it again', 700, 640, { w: 440, h: 110, color: PAL.teal, size: 56 })) this.restart();
        if (UI.button('bl-back', 'Back to the map', 1220, 640, { w: 440, h: 110, color: C.navy, size: 56 })) Game.replace(new Levels.map());
      } else if (this.canFinish() && Game.current() === this) {
        // top right, under the HUD buttons: clear of the stamp and the lyrics
        const bw = 300, bh = UI.tap(90), bx = 1920 - 40 - bw / 2, by = m.y + m.r + 28 + bh / 2;
        this.finishRect = [bx, by, bw, bh];
        if (UI.button('bl-finish', 'Finish', bx, by, { w: bw, h: 90, color: C.brick, size: 52 })) this.done();
      }
    }
  }
  Ballad.STEP = STEP;
  Ballad.TAKE = TAKE_NAME;
  Ballad.Song = Song;
  Ballad.plan = plan;
  Ballad.record = record;
  Levels.ballad = Ballad;
})();
